/**
 * PROG-IN-SPLIT/FE-PICKER (#ea563d4a) — Library picker modal for
 * PrecompiledProgression. Architect plan #d1460ba5 §9-bis.
 *
 * AntD Modal with free-text search + tag filter that lists precompiled
 * progressions from the existing paginated endpoint. On confirm, returns a
 * deep-cloned `weeks: PrecompiledWeek[]` plus source metadata so the consumer
 * (FE-CARD) can build an ExerciseProgression value. No new API.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Empty, Input, List, Modal, Space, Spin, Tag, Typography } from "antd";
import { Entities } from "../../api/Entities";
import type { PrecompiledProgression, PrecompiledWeek } from "../../types/Entities";
import type { ExerciseProgressionSource } from "../../types/Programmazione";
import type { PagedResults } from "../../types/generics";

const TAG_COLORS = ["blue", "purple", "cyan", "geekblue", "volcano", "orange"];
const tagColor = (t: string) => TAG_COLORS[t.charCodeAt(0) % TAG_COLORS.length];

const PAGE_SIZE = 50;

export interface PrecompiledProgressionPickerResult {
  weeks: PrecompiledWeek[];
  source: Extract<ExerciseProgressionSource, { kind: "library" }>;
}

export interface PrecompiledProgressionPickerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (result: PrecompiledProgressionPickerResult) => void;
}

type LibraryRow = PrecompiledProgression & { updatedAt?: string };

export default function PrecompiledProgressionPicker({
  open,
  onClose,
  onConfirm,
}: PrecompiledProgressionPickerProps) {
  const [items, setItems] = useState<LibraryRow[]>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [freeText, setFreeText] = useState("");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(false);
    const params = new URLSearchParams();
    params.append("page", "1");
    params.append("size", `${PAGE_SIZE}`);
    Entities.getPrecompiledProgressionsPaginated(params).then(
      ({ data }) => {
        setItems((data as PagedResults<LibraryRow>).items);
        setLoading(false);
      },
      () => {
        setError(true);
        setItems([]);
        setLoading(false);
      }
    );
  }, [open]);

  // Reset transient state when the modal closes.
  useEffect(() => {
    if (!open) {
      setFreeText("");
      setSelectedTag(null);
      setSelectedId(null);
    }
  }, [open]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    items?.forEach((it) => it.tags?.forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const needle = freeText.trim().toLowerCase();
    return items.filter((it) => {
      if (selectedTag && !(it.tags ?? []).includes(selectedTag)) return false;
      if (!needle) return true;
      return (
        it.label.toLowerCase().includes(needle) ||
        (it.description ?? "").toLowerCase().includes(needle)
      );
    });
  }, [items, freeText, selectedTag]);

  const handleConfirm = useCallback(() => {
    if (!items || !selectedId) return;
    const src = items.find((it) => it.id === selectedId);
    if (!src || !src.id) return;
    // Deep-clone weeks so the editor owns the snapshot independently of the
    // library row. Spec: "confirmation returns deep-cloned weeks (not a reference)".
    const clonedWeeks = JSON.parse(JSON.stringify(src.weeks)) as PrecompiledWeek[];
    onConfirm({
      weeks: clonedWeeks,
      source: {
        kind: "library",
        precompiledProgressionId: src.id,
        // libraryVersion = source.updatedAt ISO if available; undefined otherwise.
        libraryVersion: src.updatedAt,
      },
    });
  }, [items, selectedId, onConfirm]);

  return (
    <Modal
      title="Scegli progressione precompilata"
      open={open}
      onCancel={onClose}
      onOk={handleConfirm}
      okText="Importa"
      cancelText="Annulla"
      okButtonProps={{ disabled: !selectedId }}
      width={720}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Input.Search
          placeholder="Cerca per nome o descrizione"
          allowClear
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
        />

        {allTags.length > 0 && (
          <Space size={4} wrap>
            <Tag.CheckableTag
              checked={selectedTag === null}
              onChange={() => setSelectedTag(null)}
            >
              Tutti
            </Tag.CheckableTag>
            {allTags.map((t) => (
              <Tag.CheckableTag
                key={t}
                checked={selectedTag === t}
                onChange={(checked) => setSelectedTag(checked ? t : null)}
              >
                {t}
              </Tag.CheckableTag>
            ))}
          </Space>
        )}

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <Spin />
          </div>
        ) : error ? (
          <Empty description="Impossibile caricare le progressioni precompilate" />
        ) : filtered.length === 0 ? (
          <Empty description="Nessuna progressione trovata" />
        ) : (
          <List
            dataSource={filtered}
            rowKey={(item) => item.id ?? item.label}
            style={{ maxHeight: 380, overflowY: "auto" }}
            renderItem={(item) => {
              const isSelected = item.id === selectedId;
              return (
                <List.Item
                  onClick={() => setSelectedId(item.id ?? null)}
                  style={{
                    cursor: "pointer",
                    padding: "10px 12px",
                    background: isSelected ? "rgba(156, 20, 25, 0.08)" : undefined,
                    borderLeft: isSelected
                      ? "3px solid var(--app-primary, #9c1419)"
                      : "3px solid transparent",
                  }}
                  data-testid={`pp-picker-item-${item.id}`}
                >
                  <Space direction="vertical" size={2} style={{ width: "100%" }}>
                    <Typography.Text strong>{item.label}</Typography.Text>
                    {item.description && (
                      <Typography.Text type="secondary" style={{ fontSize: "0.85rem" }}>
                        {item.description}
                      </Typography.Text>
                    )}
                    <Space size={4} wrap>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {item.weeks?.length ?? 0} sett.
                      </Typography.Text>
                      {item.tags?.map((t) => (
                        <Tag
                          key={t}
                          color={tagColor(t)}
                          style={{ margin: 0, fontSize: "0.7rem" }}
                        >
                          {t}
                        </Tag>
                      ))}
                    </Space>
                  </Space>
                </List.Item>
              );
            }}
          />
        )}
      </Space>
    </Modal>
  );
}
